import { SocialConnection } from "./Components/Social/SocialConnection.tsx";
import { Alert, Button } from "@mui/material";
import { worldMatchReturnPath } from "./Components/World/worldMatchReturn.ts";
import { type FC, type ReactNode, Suspense, useContext, useEffect, useMemo, useState } from "react";
import {
	Navigate,
	Route,
	Routes,
	type To,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { ensureCsrfToken, fetchSessionUser, getStoredAuthToken } from "./Auth/serverSessionApi.ts";
import { authRoles, hasRole } from "./Auth/roles.ts";
import { LoginScreen } from "./Components/Login";
import { PasswordRecoveryScreen } from "./Components/Login/PasswordRecoveryScreen.tsx";
import { GlobalFeedbackComposer } from "./Components/Feedback/GlobalFeedbackComposer.tsx";
import type { DeckId, MatchId } from "./Domain/types.ts";
import { clientKernelContext } from "./Kernel/ClientKernel.ts";
import { useClientDispatch, useClientSelector } from "./Redux/clientHooks.ts";
import {
	clearUserId,
	setImpersonatedBy,
	setUserId,
	setUserProfile,
	setUserRole,
	userIdSelector,
	userRoleSelector,
} from "./Redux/clientSlice.ts";
import { useUiI18n } from "./I18n";
import { useSiteFeatures } from "./Components/SiteFeatures/useSiteFeatures.ts";
import { PageScene } from "./Components/Page/PageScene.tsx";
import { ErrorRegion } from "./Components/Page/ErrorRegion.tsx";

import {
	WorldEditorPage,
	Match,
	DeckBuilder,
	CollectionShopPage,
	CardBuilder,
	Dashboard,
	GameManualPage,
	ActionHistoryPage,
	CampaignAdminPage,
	CampaignPreviewOpenPage,
	CampaignSelectionPage,
	MatchBotDashboardPage,
	MatchCardInstancesPage,
	ArtStylesPage,
	ConditionalGroupsPage,
	AbilityFormulasPage,
	CardBackArtworkPage,
	AdminUsersPage,
	BotPersonasPage,
	GameSettingsProfilesPage,
	TranslationsPage,
	LogDashboardPage,
	StyleGuidePages,
	ProfilePage,
	PortraitAtelierPage,
	FeedbackPage,
	SiteFeaturesAdminPage,
	DesignAssetsPage,
	DesignAssetsHelpPage,
	DeveloperDashboardPage,
	InvitationPage,
	WorldPlayer,
	WorldGroupEntry,
	FriendsPage,
	WorldSelectionPage,
} from "./AppRouteScreens.ts";

export const useAppUrls = () => {
	const userId = useClientSelector(userIdSelector);
	const userRole = useClientSelector(userRoleSelector);
	const features = useSiteFeatures();
	return useMemo(() => {
		const isAdmin = hasRole(userRole, authRoles.admin);
		const isEditor = hasRole(userRole, authRoles.editor);
		return {
			login: "/",
			match: (match: MatchId, deckId?: DeckId): To | null => {
				if (!userId) return null;
				const query = deckId ? `?deckId=${encodeURIComponent(deckId as unknown as string)}` : "";
				return `/matches/${match}/play${query}`;
			},
			spectateMatch: (match: MatchId): To | null =>
				userId ? `/matches/${match}/play?spectator=1` : null,
			matchBotDashboard: (match: MatchId): To | null =>
				userId ? `/matches/${match}/bot-dashboard?tab=bot-personas` : null,
			matchCardInstances: (match: MatchId): To | null =>
				userId ? `/matches/${match}/card-instances` : null,
			lobby: (match: MatchId): To | null => (userId ? `/matches/${match}/lobby` : null),
			dashboard: userId ? "/dashboard" : null,
			profile: userId ? "/profile" : null,
			portraitAtelier: isEditor ? "/editor/portraits" : null,
			worldEditor: isEditor ? "/admin/worlds" : null,
			worlds: userId && features?.worldsEnabled ? "/worlds" : null,
			friends: userId ? "/friends" : null,
			designAssets: isEditor ? "/editor/design-assets" : null,
			feedback: userId ? "/feedback" : null,
			deckbuilder: userId ? "/deckbuilder" : null,
			collectionShop: userId ? "/collection" : null,
			gameManual: userId ? "/game-manual" : null,
			cardbuilder: isEditor ? "/cards" : null,
			artStyles: isEditor ? "/art-styles" : null,
			artStyleGroups: isEditor ? "/art-styles/groups" : null,
			abilityFormulas: isEditor ? "/ability-formulas" : null,
			botPersonas: isEditor ? "/bot-personas" : null,
			gameSettingsProfiles: isEditor ? "/game-settings-profiles" : null,
			adminCampaign: isEditor ? "/admin/campaign" : null,
			translations: isEditor ? "/translations" : null,
			actionHistory: userId ? "/analysis" : null,
			analysis: (historyId: string): To | null =>
				userId ? `/analysis/${encodeURIComponent(historyId)}` : null,
			styleGuide: isEditor ? "/styleguide" : null,
			adminUsers: isAdmin ? "/admin/users" : null,
			adminCardBacks: isAdmin ? "/admin/card-backs" : null,
			siteFeatures: isAdmin ? "/admin/site-features" : null,
			logs: isAdmin ? "/admin/logs" : null,
			graphDashboard: isAdmin ? "/admin/graphql-dashboard" : null,
			colyseusDashboard: isAdmin ? "/admin/colyseus-dashboard" : null,
			campaign: userId ? "/campaign" : null,
		};
	}, [userId, userRole, features?.worldsEnabled]);
};

export const useAppNavigate = () => {
	const navigate = useNavigate();
	const urls = useAppUrls();
	const { t } = useUiI18n();
	return useMemo(() => {
		const route = (url: To | null) => {
			if (!url) {
				toast.warn(t("nav.loginRequired"));
				return;
			}
			return navigate(url);
		};

		return {
			login: () => route(urls.login),
			match: (match: MatchId, deckId?: DeckId) => route(urls.match(match, deckId)),
			spectateMatch: (match: MatchId) => route(urls.spectateMatch(match)),
			matchBotDashboard: (match: MatchId) => route(urls.matchBotDashboard(match)),
			matchCardInstances: (match: MatchId) => route(urls.matchCardInstances(match)),
			lobby: (match: MatchId) => route(urls.lobby(match)),
			dashboard: () => route(urls.dashboard),
			profile: () => route(urls.profile),
			portraitAtelier: () => route(urls.portraitAtelier),
			designAssets: () => route(urls.designAssets),
			feedback: () => route(urls.feedback),
			deckbuilder: () => route(urls.deckbuilder),
			collectionShop: () => route(urls.collectionShop),
			gameManual: () => route(urls.gameManual),
			cardbuilder: () => route(urls.cardbuilder),
			artStyles: () => route(urls.artStyles),
			artStyleGroups: () => route(urls.artStyleGroups),
			abilityFormulas: () => route(urls.abilityFormulas),
			botPersonas: () => route(urls.botPersonas),
			gameSettingsProfiles: () => route(urls.gameSettingsProfiles),
			campaign: () => route(urls.campaign),
			adminCampaign: () => route(urls.adminCampaign),
			translations: () => route(urls.translations),
			actionHistory: () => route(urls.actionHistory),
			analysis: (historyId: string) => route(urls.analysis(historyId)),
			adminUsers: () => route(urls.adminUsers),
			adminCardBacks: () => route(urls.adminCardBacks),
			logs: () => route(urls.logs),
			graphDashboard: () => route(urls.graphDashboard),
			colyseusDashboard: () => route(urls.colyseusDashboard),
		};
	}, [navigate, t, urls]);
};

const RequireSession: FC<{ children: ReactNode }> = ({ children }) => {
	const userId = useClientSelector(userIdSelector);
	if (!userId) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
};

const RequireRole: FC<{ children: ReactNode; role: "admin" | "editor" }> = ({ children, role }) => {
	const userRole = useClientSelector(userRoleSelector);
	if (!hasRole(userRole, role)) {
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
};

const RequireFeature: FC<{
	children: ReactNode;
	feature: "shopEnabled" | "campaignEnabled" | "worldsEnabled";
}> = ({ children, feature }) => {
	const features = useSiteFeatures();
	if (!features) return null;
	return features[feature] ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const LoginRoute: FC = () => {
	const userId = useClientSelector(userIdSelector);
	if (userId) {
		return <Navigate to="/dashboard" replace />;
	}

	return <LoginScreen />;
};
const FriendsRoute: FC = () => {
	const userId = useClientSelector(userIdSelector),
		location = useLocation();
	return userId ? <FriendsPage /> : <LoginScreen returnTo={location.pathname + location.search} />;
};

const LegacyAnalysisRedirect: FC = () => {
	const { id } = useParams();
	const location = useLocation();
	return (
		<Navigate to={`/analysis${id ? `/${encodeURIComponent(id)}` : ""}${location.search}`} replace />
	);
};

export function AppRoutes() {
	const kernel = useContext(clientKernelContext);
	if (!kernel) {
		throw new Error("Client kernel context is required");
	}

	const dispatch = useClientDispatch();
	const [sessionResolved, setSessionResolved] = useState(false);
	const location = useLocation();
	const urls = useAppUrls();
	const navigate = useNavigate();
	const { t } = useUiI18n();

	useEffect(() => {
		let active = true;
		void Promise.all([ensureCsrfToken(), fetchSessionUser()])
			.then(async ([, user]) => {
				if (!active) return;
				if (user) {
					dispatch(setUserId(user.id));
					dispatch(setUserProfile({ username: user.username, language: user.language }));
					dispatch(setUserRole(user.role));
					dispatch(setImpersonatedBy(user.impersonatedBy));
					kernel.matchesClient.setAuthToken(getStoredAuthToken());
					return;
				}
				await kernel.client.clearStore();
				dispatch(clearUserId());
				kernel.matchesClient.setAuthToken(null);
			})
			.catch(async () => {
				if (!active) return;
				await kernel.client.clearStore();
				dispatch(clearUserId());
				kernel.matchesClient.setAuthToken(null);
			})
			.finally(() => {
				if (active) {
					setSessionResolved(true);
				}
			});

		return () => {
			active = false;
		};
	}, [dispatch, kernel]);

	if (!sessionResolved) {
		return null;
	}

	const routes = (
		<Routes>
			<Route path="/invitations/:invitationId" element={<InvitationPage />} />
			<Route path="/friends" element={<FriendsRoute />} />
			<Route
				path="/admin/worlds/:worldId?"
				element={
					<RequireSession>
						<WorldEditorPage />
					</RequireSession>
				}
			/>
			<Route
				path="/worlds"
				element={
					<RequireSession>
						<RequireFeature feature="worldsEnabled">
							<WorldSelectionPage />
						</RequireFeature>
					</RequireSession>
				}
			/>
			<Route
				path="/worlds/:worldId"
				element={
					<RequireSession>
						<RequireFeature feature="worldsEnabled">
							<WorldPlayer />
						</RequireFeature>
					</RequireSession>
				}
			/>
			<Route
				path="/world-sessions/:sessionId"
				element={
					<RequireSession>
						<RequireFeature feature="worldsEnabled">
							<WorldGroupEntry />
						</RequireFeature>
					</RequireSession>
				}
			/>

			<Route path="/" element={<LoginRoute />} />
			<Route path="/forgot-password" element={<PasswordRecoveryScreen />} />
			<Route path="/reset-password" element={<PasswordRecoveryScreen />} />
			<Route
				path="/matches/:matchId/lobby"
				element={
					<RequireSession>
						<Match />
					</RequireSession>
				}
			/>
			<Route
				path="/matches/:matchId/play"
				element={
					<RequireSession>
						<Match />
					</RequireSession>
				}
			/>
			<Route
				path="/matches/:matchId/bot-dashboard"
				element={
					<RequireSession>
						<MatchBotDashboardPage />
					</RequireSession>
				}
			/>
			<Route
				path="/matches/:matchId/card-instances"
				element={
					<RequireSession>
						<MatchCardInstancesPage />
					</RequireSession>
				}
			/>
			<Route
				path="/deckbuilder"
				element={
					<RequireSession>
						<DeckBuilder />
					</RequireSession>
				}
			/>
			<Route
				path="/collection"
				element={
					<RequireSession>
						<RequireFeature feature="shopEnabled">
							<CollectionShopPage />
						</RequireFeature>
					</RequireSession>
				}
			/>
			<Route
				path="/game-manual"
				element={
					<RequireSession>
						<GameManualPage />
					</RequireSession>
				}
			/>
			<Route
				path="/cards"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<CardBuilder />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/art-styles"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<ArtStylesPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/art-styles/groups"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<ConditionalGroupsPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/ability-formulas"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<AbilityFormulasPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/bot-personas"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<BotPersonasPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/game-settings-profiles"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<GameSettingsProfilesPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/site-features"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<SiteFeaturesAdminPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/campaign"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<CampaignAdminPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/campaign/:campaignId"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<CampaignAdminPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/campaign-preview/open"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<CampaignPreviewOpenPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/translations"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<TranslationsPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/analysis"
				element={
					<RequireSession>
						<ActionHistoryPage />
					</RequireSession>
				}
			/>
			<Route
				path="/analysis/:id"
				element={
					<RequireSession>
						<ActionHistoryPage />
					</RequireSession>
				}
			/>
			<Route path="/match-archive" element={<LegacyAnalysisRedirect />} />
			<Route path="/match-archive/:id" element={<LegacyAnalysisRedirect />} />
			<Route path="/match-snapshots" element={<LegacyAnalysisRedirect />} />
			<Route path="/action-history" element={<LegacyAnalysisRedirect />} />
			<Route path="/action-history/:id" element={<LegacyAnalysisRedirect />} />
			<Route
				path="/dashboard"
				element={
					<RequireSession>
						<Dashboard />
					</RequireSession>
				}
			/>
			<Route
				path="/profile"
				element={
					<RequireSession>
						<ProfilePage />
					</RequireSession>
				}
			/>
			<Route
				path="/editor/portraits"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<PortraitAtelierPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/editor/design-assets"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<DesignAssetsPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/editor/design-assets/help"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<DesignAssetsHelpPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/feedback"
				element={
					<RequireSession>
						<FeedbackPage />
					</RequireSession>
				}
			/>
			<Route path="/campaigns" element={<Navigate to="/campaign" replace />} />
			<Route
				path="/campaign"
				element={
					<RequireSession>
						<RequireFeature feature="campaignEnabled">
							<CampaignSelectionPage />
						</RequireFeature>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/card-backs"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<CardBackArtworkPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/users"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<AdminUsersPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/logs"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<LogDashboardPage />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/graphql-dashboard"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<DeveloperDashboardPage title={t("dashboard.graphDashboard")} source="/playground" />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/admin/colyseus-dashboard"
				element={
					<RequireSession>
						<RequireRole role="admin">
							<DeveloperDashboardPage title={t("dashboard.colyseusDashboard")} source="/colyseus" />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route
				path="/styleguide"
				element={
					<RequireSession>
						<RequireRole role="editor">
							<StyleGuidePages />
						</RequireRole>
					</RequireSession>
				}
			/>
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
	const routeContent = (
		<Suspense
			fallback={
				<Alert severity="info" role="status">
					{t("world.loading")}
				</Alert>
			}
		>
			{routes}
		</Suspense>
	);
	const sceneTitles: Record<string, string> = {
		"/admin/worlds": t("world.editorTitle"),
		"/worlds": t("world.worlds"),
		"/friends": t("social.title"),
		"/cards": t("dashboard.cardBuilder"),
		"/art-styles": t("dashboard.artStyles"),
		"/art-styles/groups": t("artStyle.groupsTitle"),
		"/ability-formulas": t("dashboard.abilityFormulas"),
		"/bot-personas": t("dashboard.botPersonas"),
		"/game-settings-profiles": t("dashboard.gameProfiles"),
		"/editor/portraits": t("profile.editorTitle"),
		"/editor/design-assets": t("dashboard.designAssets"),
		"/editor/design-assets/help": t("dashboard.designAssetsHelp"),
		"/admin/campaign": t("dashboard.editCampaign"),
		"/translations": t("dashboard.translations"),
		"/styleguide": t("dashboard.styleGuide"),
		"/admin/card-backs": t("dashboard.cardBackArtwork"),
		"/admin/users": t("dashboard.manageUsers"),
		"/admin/logs": t("dashboard.applicationLogs"),
		"/admin/graphql-dashboard": t("dashboard.graphDashboard"),
		"/admin/colyseus-dashboard": t("dashboard.colyseusDashboard"),
		"/admin/site-features": t("dashboard.siteFeatures"),
		"/analysis": t("dashboard.analysis"),
		"/game-manual": t("dashboard.gameManual"),
	};
	const sceneTitle =
		sceneTitles[location.pathname] ??
		(location.pathname.startsWith("/admin/worlds/")
			? t("world.editorTitle")
			: location.pathname.startsWith("/worlds/")
				? null
				: location.pathname.startsWith("/invitations/")
					? t("social.invitationsTitle")
					: null) ??
		(location.pathname.startsWith("/admin/campaign/") ? t("dashboard.editCampaign") : null);
	const sceneBackUrl =
		location.pathname === "/art-styles/groups"
			? "/art-styles"
			: location.pathname === "/editor/design-assets/help"
				? "/editor/design-assets"
				: undefined;
	const worldReturnPath = location.pathname.startsWith("/matches/")
		? worldMatchReturnPath(
				location.pathname.split("/")[2],
				sessionStorage.getItem("moc-world-match"),
				sessionStorage.getItem("moc-world-return"),
			)
		: null;
	return (
		<>
			{worldReturnPath && (
				<Button
					onClick={() => navigate(worldReturnPath)}
					variant="contained"
					sx={{
						position: "fixed",
						right: 16,
						bottom: 16,
						zIndex: (theme) => theme.zIndex.modal - 1,
					}}
				>
					{t("world.return")}
				</Button>
			)}
			{sceneTitle ? (
				<PageScene
					title={sceneTitle}
					backUrl={sceneBackUrl}
					links={[
						{ label: t("common.backToDashboard"), url: urls.dashboard },
						{ label: t("page.deckbuilder"), url: urls.deckbuilder },
						{ label: t("page.collection"), url: urls.collectionShop },
						{ label: t("page.gameManual"), url: urls.gameManual },
						{ label: t("page.analysis"), url: urls.actionHistory },
						{ label: t("page.campaign"), url: urls.campaign },
						{ label: t("feedback.pageTitle"), url: urls.feedback },
						{ label: t("page.cards"), url: urls.cardbuilder },
						{ label: t("page.abilityFormulas"), url: urls.abilityFormulas },
						{ label: t("page.artStyles"), url: urls.artStyles },
						{ label: t("page.botPersonas"), url: urls.botPersonas },
						{ label: t("page.gameSettings"), url: urls.gameSettingsProfiles },
						{ label: t("profile.editorTitle"), url: urls.portraitAtelier },
						{ label: t("dashboard.editCampaign"), url: urls.adminCampaign },
						{ label: t("dashboard.translations"), url: urls.translations },
						{ label: t("dashboard.styleGuide"), url: urls.styleGuide },
						{ label: t("world.editorTitle"), url: urls.worldEditor },
						{ label: t("world.worlds"), url: urls.worlds },
						{ label: t("social.title"), url: urls.friends },
						{ label: t("dashboard.designAssets"), url: urls.designAssets },
						{ label: t("dashboard.cardBackArtwork"), url: urls.adminCardBacks },
						{ label: t("dashboard.siteFeatures"), url: urls.siteFeatures },
						{ label: t("dashboard.manageUsers"), url: urls.adminUsers },
						{ label: t("dashboard.applicationLogs"), url: urls.logs },
						{ label: t("dashboard.graphDashboard"), url: urls.graphDashboard },
						{ label: t("dashboard.colyseusDashboard"), url: urls.colyseusDashboard },
						{ label: t("page.profile"), url: urls.profile },
					]}
				>
					{routeContent}
				</PageScene>
			) : (
				<ErrorRegion key={location.pathname} name={location.pathname}>
					{routeContent}
				</ErrorRegion>
			)}
			<SocialConnection />
			<GlobalFeedbackComposer />
		</>
	);
}
